import type {
  CustomerMonthlyRollup,
  ForecastMonthAmount,
  ForecastMonthMetric,
  Opportunity,
  OpportunityMonth,
} from '@/api/types'

export const MONTH_STRIP_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

export const MONTH_METRIC_OPTIONS: Array<{ value: ForecastMonthMetric; label: string }> = [
  { value: 'committed', label: 'Committed' },
  { value: 'weighted', label: 'Weighted' },
  { value: 'upside', label: 'Upside' },
]

export function monthMetricAmount(
  row: ForecastMonthAmount | undefined,
  metric: ForecastMonthMetric,
): number {
  if (!row) return 0
  const raw = row[metric]
  const numeric = typeof raw === 'string' ? Number(raw) : raw
  return Number.isFinite(numeric) ? numeric : 0
}

export function amountsForMetric(
  months: ForecastMonthAmount[] | undefined,
  metric: ForecastMonthMetric,
): number[] {
  const byMonth = new Map((months ?? []).map((row) => [row.month, row]))
  return MONTH_STRIP_LABELS.map((_, index) => monthMetricAmount(byMonth.get(index + 1), metric))
}

function isCommittedCategory(category: string | null | undefined): boolean {
  return (category ?? '').toLowerCase() === 'committed'
}

function isLostCategory(category: string | null | undefined): boolean {
  return (category ?? '').toLowerCase() === 'lost'
}

/** Build forecast-year month rollups from materialized opportunity months. */
export function opportunityMonthsToForecastAmounts(
  months: OpportunityMonth[] | undefined,
  year: number,
  probability: number,
  stageCategory: string | null | undefined,
): ForecastMonthAmount[] {
  const byMonth = new Map<number, number>()
  for (const row of months ?? []) {
    if (row.year !== year) continue
    const amount = Number(row.amount)
    if (!Number.isFinite(amount)) continue
    byMonth.set(row.month, (byMonth.get(row.month) ?? 0) + amount)
  }
  const prob = Number.isFinite(probability) ? probability : 0
  const committed = isCommittedCategory(stageCategory)
  const lost = isLostCategory(stageCategory)
  return MONTH_STRIP_LABELS.map((_, index) => {
    const month = index + 1
    const amount = lost ? 0 : (byMonth.get(month) ?? 0)
    return {
      month,
      committed: committed ? amount : 0,
      weighted: amount * prob,
      upside: amount,
    }
  })
}

export function lineYearTotalForMetric(
  item: Pick<Opportunity, 'year_total' | 'weighted_total' | 'probability' | 'stage_category'>,
  metric: ForecastMonthMetric,
): number {
  const unweighted = Number(item.year_total)
  const base = Number.isFinite(unweighted) ? unweighted : 0
  const weightedRaw = Number(item.weighted_total)
  const weighted = Number.isFinite(weightedRaw) ? weightedRaw : base * Number(item.probability ?? 0)
  // Lost is excluded from Upside / Weighted / Committed portfolio math — never show
  // unweighted revenue for lost lines when those metrics are selected.
  if (isLostCategory(item.stage_category)) {
    return 0
  }
  if (metric === 'committed') {
    return isCommittedCategory(item.stage_category) ? base : 0
  }
  if (metric === 'weighted') {
    return weighted
  }
  return base
}

/** Sum line metrics for a customer group when rollup data is missing. */
export function sumLineYearTotalsForMetric(
  items: Array<
    Pick<Opportunity, 'year_total' | 'weighted_total' | 'probability' | 'stage_category'>
  >,
  metric: ForecastMonthMetric,
): number {
  return items.reduce((sum, item) => sum + lineYearTotalForMetric(item, metric), 0)
}

export function customerYearTotalForMetric(
  customer: CustomerMonthlyRollup | undefined,
  metric: ForecastMonthMetric,
): number | null {
  if (!customer) return null
  const raw =
    metric === 'committed'
      ? customer.year_committed
      : metric === 'weighted'
        ? customer.year_weighted
        : customer.year_upside
  const numeric = typeof raw === 'string' ? Number(raw) : Number(raw)
  return Number.isFinite(numeric) ? numeric : 0
}
